import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import './PlanWeek.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const fmtDate = (d) =>
  d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtTime = (t) => (t ? t.slice(0, 5) : '');
const fmtPrice = (p) => `₹${parseFloat(p).toFixed(2)}`;

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

const fmtWeekLabel = (days) => {
  const s = days[0].toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  const e = days[days.length - 1].toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${s} – ${e}`;
};

// Mon–Sun of the week at `offset` weeks from current
const getWeekDays = (offset = 0) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dow = today.getDay(); // 0=Sun, 1=Mon … 6=Sat
  const daysToMonday = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(today);
  monday.setDate(today.getDate() + daysToMonday + offset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
};

// Before 22:00 → editable from tomorrow; 22:00+ → editable from day-after-tomorrow
const getEarliestEditable = () => {
  const now = new Date();
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + (now.getHours() < 22 ? 1 : 2));
  return d;
};

// Each line carries its own category
const emptyLine = () => ({
  category: null,
  product_id: null,
  qty: 1,
  unit_price: 0,
  product_name: '',
});

const EARLIEST_EDITABLE = getEarliestEditable();

const defaultDayState = () => ({
  window: null,
  lines: Array.from({ length: 5 }, emptyLine),
  savedOrderId: null,
  saved: false,
  saving: false,
  saveError: null,
  moveTo: '',
  moving: false,
  cancelling: false,
  order_lc_state: null,
  cancelSuccess: false,
  cancelledAmount: 0,
});

// ─────────────────────────────────────────────────────────
// Day summary card
// ─────────────────────────────────────────────────────────
const DayCard = ({ d, ds, onOpen, editable, onReplicate, replicating, onReschedule, onCancelOrder }) => {
  const activeLines = ds.lines.filter((l) => l.product_id);
  const total = activeLines.reduce((s, l) => s + (l.unit_price || 0) * (l.qty || 0), 0);
  const [confirming, setConfirming] = useState(false);
  const isPlaced = ds.order_lc_state === 'Placed';

  return (
    <div className={`pw-card${ds.saved ? ' pw-card--saved' : ''}${!editable ? ' pw-card--past' : ''}`}>
      <div className="pw-card-date">
        <span>{fmtDate(d)}</span>
        {ds.saved && <span className="pw-saved-badge">Saved</span>}
      </div>

      {ds.cancelSuccess ? (
        <div className="pw-cancel-success-card">
          Your wallet has been credited with <strong>{fmtPrice(ds.cancelledAmount)}</strong>.
        </div>
      ) : (
        <>
          {ds.saved ? (
            <div className="pw-card-summary">
              {ds.window && <div className="pw-summary-window">&#128336; {ds.window}</div>}
              <ul className="pw-summary-lines">
                {activeLines.map((l, i) => (
                  <li key={i} className="pw-summary-line">
                    <span className="pw-summary-line-name">{l.product_name}</span>
                    <span className="pw-summary-line-right">
                      <span className="pw-summary-line-total">
                        {l.qty > 1 ? `×${l.qty} ` : ''}{fmtPrice(l.unit_price * l.qty)}
                      </span>
                      <button
                        className="pw-replicate-btn"
                        title="Replicate to other days this week"
                        onClick={() => onReplicate(isoDate(d), l)}
                        disabled={replicating}
                      >
                        &#x2295;
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="pw-summary-total">Total: {fmtPrice(total)}</div>
            </div>
          ) : (
            <div className="pw-card-empty">{editable ? 'No plan yet.' : '—'}</div>
          )}

          {confirming && isPlaced ? (
            <div className="pw-card-cancel-confirm">
              <p>
                This order will be cancelled and <strong>{fmtPrice(total)}</strong> will be credited to your wallet.
              </p>
              <div className="pw-card-cancel-confirm-btns">
                <button
                  className="pw-card-btn pw-card-btn--cancel-no"
                  onClick={() => setConfirming(false)}
                  disabled={ds.cancelling}
                >
                  Go Back
                </button>
                <button
                  className="pw-card-btn pw-card-btn--cancel-yes"
                  onClick={() => { onCancelOrder(isoDate(d)); setConfirming(false); }}
                  disabled={ds.cancelling}
                >
                  {ds.cancelling ? 'Cancelling…' : 'Confirm'}
                </button>
              </div>
            </div>
          ) : (
            <div className="pw-card-actions">
              {editable && (!ds.saved || ds.order_lc_state === 'Draft') && (
                <button className="pw-card-btn" onClick={() => onOpen(isoDate(d))}>
                  {ds.saved ? 'Edit Plan' : 'Create Plan'}
                </button>
              )}
              {ds.saved && isPlaced && (
                <div className="pw-card-placed-row">
                  <button className="pw-card-btn pw-card-btn--reschedule" onClick={() => onReschedule(isoDate(d))}>
                    Re-schedule
                  </button>
                  <button
                    className="pw-card-btn pw-card-btn--cancel"
                    onClick={() => setConfirming(true)}
                    disabled={ds.cancelling}
                  >
                    Cancel Order
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Plan modal (create / edit) — two-column redesign
// ─────────────────────────────────────────────────────────
const catLabel = (name) => {
  const idx = name.indexOf('-');
  return idx > 0 ? name.slice(0, idx).trim() : name;
};

const PlanModal = ({
  dateKey,
  ds,
  menus,
  windows,
  products,
  submenus,
  minOrderValue,
  onFetchProducts,
  onFetchSubmenus,
  onUpdateDay,
  onSave,
  onClose,
}) => {
  const [hy, hm, hd] = dateKey.split('-').map(Number);
  const dayDate = new Date(hy, hm - 1, hd);

  const [selectedCategory, setSelectedCategory] = useState(() => menus[0]?.id || null);
  const [selectedSubmenu, setSelectedSubmenu] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState(() => {
    const c = {};
    (ds.lines || []).filter((l) => l.product_id && l.qty > 0).forEach((l) => {
      c[l.product_id] = {
        qty: parseInt(l.qty, 10),
        unit_price: parseFloat(l.unit_price),
        product_name: l.product_name,
        category: l.category,
        delivery_fee_exempt: l.delivery_fee_exempt === 'Y',
      };
    });
    return c;
  });

  useEffect(() => {
    if (selectedCategory) {
      onFetchProducts(selectedCategory, dateKey);
      onFetchSubmenus(selectedCategory);
    }
    setSelectedSubmenu(null);
  }, [selectedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select the 9:00 window for new days (no window chosen yet).
  useEffect(() => {
    if (ds.window !== null || windows.length === 0) return;
    const w = windows.find((w) => w.start_time?.startsWith('09')) || windows[0];
    onUpdateDay(dateKey, { window: `${fmtTime(w.start_time)} - ${fmtTime(w.end_time)}` });
  }, [windows]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleWindow = (label) =>
    onUpdateDay(dateKey, { window: ds.window === label ? null : label });

  const setQty = (product, newQty) => {
    if (newQty <= 0) {
      setCart((prev) => {
        const next = { ...prev };
        delete next[product.id];
        return next;
      });
    } else {
      setCart((prev) => ({
        ...prev,
        [product.id]: {
          qty: newQty,
          unit_price: parseFloat(product.price),
          product_name: product.name,
          category: selectedCategory,
          delivery_fee_exempt: product.delivery_fee_exempt === 'Y',
        },
      }));
    }
  };

  const handleSave = () => {
    const lines = Object.entries(cart)
      .filter(([, v]) => v.qty > 0)
      .map(([product_id, v]) => ({
        product_id: parseInt(product_id, 10),
        qty: v.qty,
        unit_price: v.unit_price,
        product_name: v.product_name,
        category: v.category,
      }));
    const hasExemptItem = Object.values(cart).some((v) => v.delivery_fee_exempt);
    if (lines.length > 0 && minOrderValue > 0 && cartTotal < minOrderValue && !hasExemptItem) {
      onUpdateDay(dateKey, { saveError: `Add ₹${(minOrderValue - cartTotal).toFixed(0)} more` });
      return;
    }
    onSave(dateKey, lines);
  };

  const cartTotal = Object.values(cart).reduce((s, v) => s + v.unit_price * v.qty, 0);
  const cartCount = Object.values(cart).reduce((s, v) => s + v.qty, 0);
  const catSubmenus = submenus[selectedCategory] || [];
  const activeMenuId = selectedSubmenu || selectedCategory;
  const productsCacheKey = `${activeMenuId}:${dateKey}`;
  const currentProducts = products[productsCacheKey] || [];
  const productsLoaded = productsCacheKey in products;

  const handleSubmenuSelect = (submenuId) => {
    setSelectedSubmenu(submenuId);
    if (submenuId) onFetchProducts(submenuId, dateKey);
  };

  return (
    <div className="pw-modal-overlay" onClick={onClose}>
      <div className="pw-modal pw-modal--plan" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="pw-modal-header pw-plan-header">
          <h3 className="pw-modal-title">{fmtDate(dayDate)}</h3>
          {windows.length > 0 && (
            <div className="pw-plan-windows">
              <span className="pw-plan-window-label">Window <span className="pw-required">*</span></span>
              {windows.map((w, wi) => {
                const label = `${fmtTime(w.start_time)} - ${fmtTime(w.end_time)}`;
                return (
                  <button
                    key={wi}
                    className={`pw-window-pill${ds.window === label ? ' pw-window-pill--active' : ''}`}
                    onClick={() => toggleWindow(label)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          <div className="pw-header-cart-area">
            {cartCount > 0 && (
              <button
                className="pw-header-clear-btn"
                onClick={() => { setCart({}); setCartOpen(false); }}
                title="Clear all items"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14H6L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4h6v2"/>
                </svg>
              </button>
            )}
            <button
              className={`pw-header-cart-btn${cartOpen ? ' pw-header-cart-btn--open' : ''}`}
              onClick={() => setCartOpen((o) => !o)}
              title={cartCount > 0 ? `${cartCount} item${cartCount !== 1 ? 's' : ''} · ${fmtPrice(cartTotal)}` : 'Cart empty'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
              {cartCount > 0 && <span className="pw-header-cart-count">{cartCount}</span>}
            </button>
          </div>
          <button className="pw-modal-close" onClick={onClose} disabled={ds.saving}>&#215;</button>
        </div>

        {ds.saveError && (
          <div className="pw-plan-error-bar">{ds.saveError}</div>
        )}

        {/* Cart dropdown panel — anchored below the header */}
        {cartOpen && (
          <div className="pw-cart-panel">
            <div className="pw-cart-panel-header">
              <span>Cart · {fmtPrice(cartTotal)}</span>
              <button className="pw-cart-panel-close" onClick={() => setCartOpen(false)}>&#215;</button>
            </div>
            {cartCount === 0 ? (
              <p className="pw-cart-empty">No items added yet.</p>
            ) : (
              Object.entries(cart).map(([pid, v]) => {
                const p = { id: parseInt(pid, 10), price: v.unit_price, name: v.product_name };
                return (
                  <div key={pid} className="pw-cart-item">
                    <span className="pw-cart-item-name" title={v.product_name}>{v.product_name}</span>
                    <div className="pw-cart-item-controls">
                      <button className="pw-qty-btn" onClick={() => setQty(p, v.qty - 1)}>−</button>
                      <span className="pw-qty-val">{v.qty}</span>
                      <button className="pw-qty-btn" onClick={() => setQty(p, v.qty + 1)}>+</button>
                    </div>
                    <span className="pw-cart-item-total">{fmtPrice(v.unit_price * v.qty)}</span>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Body */}
        <div className="pw-plan-body">

          {/* Left: category sidebar */}
          <div className="pw-cat-sidebar">
            {menus.map((cat) => {
              const catQty = Object.values(cart)
                .filter((v) => v.category === cat.id)
                .reduce((s, v) => s + v.qty, 0);
              return (
                <button
                  key={cat.id}
                  className={`pw-cat-item${selectedCategory === cat.id ? ' pw-cat-item--active' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <div className="pw-cat-img-wrap">
                    {cat.image_url
                      ? <img src={resolveImg(cat.image_url)} alt={cat.menu_name} className="pw-cat-img" />
                      : <div className="pw-cat-img-placeholder">{(cat.header_text || catLabel(cat.menu_name))[0]}</div>}
                    {catQty > 0 && <span className="pw-cat-badge">{catQty}</span>}
                  </div>
                  <span className="pw-cat-label">{cat.header_text || catLabel(cat.menu_name)}</span>
                </button>
              );
            })}
          </div>

          {/* Right: product grid */}
          <div className="pw-products-area">
            {catSubmenus.length > 0 && (
              <div className="pw-submenu-bar">
                <button
                  className={`pw-submenu-pill${!selectedSubmenu ? ' pw-submenu-pill--active' : ''}`}
                  onClick={() => setSelectedSubmenu(null)}
                >
                  All
                </button>
                {catSubmenus.map((sm) => (
                  <button
                    key={sm.id}
                    className={`pw-submenu-pill${selectedSubmenu === sm.id ? ' pw-submenu-pill--active' : ''}`}
                    onClick={() => handleSubmenuSelect(sm.id)}
                  >
                    {sm.menu_name}
                  </button>
                ))}
              </div>
            )}
            {currentProducts.length === 0 ? (
              <p className="pw-products-empty">{productsLoaded ? 'No products' : 'Loading…'}</p>
            ) : currentProducts.some((p) => p.show_image_only) ? (
              <div className="pw-img-only-grid">
                {currentProducts.map((product) => {
                  const qty = cart[product.id]?.qty || 0;
                  return (
                    <div
                      key={product.id}
                      className={`pw-img-only-card${qty > 0 ? ' pw-img-only-card--selected' : ''}`}
                    >
                      <div className="pw-img-only-image-wrap">
                        {product.image_url
                          ? <img src={resolveImg(product.image_url)} alt={product.name} className="pw-img-only-image" />
                          : <div className="pw-img-only-placeholder">🥗</div>}
                      </div>
                      <div className="pw-img-only-footer">
                        <div className="pw-img-only-meta">
                          <span className="pw-img-only-name" title={product.name}>{product.name}</span>
                          <span className="pw-img-only-price">{fmtPrice(product.price)}</span>
                        </div>
                        <div className="pw-img-only-qty">
                          {qty > 0 ? (
                            <>
                              <button className="pw-qty-btn" onClick={() => setQty(product, qty - 1)}>−</button>
                              <span className="pw-img-only-qty-val">{qty}</span>
                              <button className="pw-qty-btn" onClick={() => setQty(product, qty + 1)}>+</button>
                            </>
                          ) : (
                            <button className="pw-add-product-btn" onClick={() => setQty(product, 1)}>+ Add</button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="pw-product-grid">
                {currentProducts.map((product) => {
                  const qty = cart[product.id]?.qty || 0;
                  return (
                    <div
                      key={product.id}
                      className={`pw-product-card${qty > 0 ? ' pw-product-card--selected' : ''}`}
                    >
                      <div className="pw-product-img-wrap">
                        {product.image_url
                          ? <img src={resolveImg(product.image_url)} alt={product.name} className="pw-product-img" />
                          : <div className="pw-product-img-placeholder">🥗</div>}
                      </div>
                      <div className="pw-product-info">
                        <div className="pw-product-name" title={product.name}>{product.name}</div>
                        <div className="pw-product-price">{fmtPrice(product.price)}</div>
                      </div>
                      <div className="pw-qty-row">
                        {qty > 0 ? (
                          <>
                            <button className="pw-qty-btn" onClick={() => setQty(product, qty - 1)}>−</button>
                            <span className="pw-qty-val">{qty}</span>
                            <button className="pw-qty-btn" onClick={() => setQty(product, qty + 1)}>+</button>
                          </>
                        ) : (
                          <button className="pw-add-product-btn" onClick={() => setQty(product, 1)}>+ Add</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="pw-modal-footer pw-plan-footer">
          <button className="pw-modal-cancel" onClick={onClose} disabled={ds.saving}>
            Cancel
          </button>
          <button className="pw-save-btn" onClick={handleSave} disabled={ds.saving}>
            {ds.saving ? 'Saving…' : ds.savedOrderId ? 'Update Plan' : 'Save Plan'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Re-schedule modal (read-only order + date picker)
// ─────────────────────────────────────────────────────────
const RescheduleModal = ({ dateKey, ds, bookedDates, onMove, onCancelOrder, onClose }) => {
  const [moveTo, setMoveTo] = useState('');
  const [confirming, setConfirming] = useState(false);

  // Build header date from the dateKey string (local time, no timezone shift)
  const [hy, hm, hd] = dateKey.split('-').map(Number);
  const headerDate = new Date(hy, hm - 1, hd);

  // Generate next 60 days from EARLIEST_EDITABLE, excluding the source date
  // and any date that already has a PYW order (bookedDates).
  const availableDates = useMemo(() => {
    if (bookedDates === null) return null; // still loading
    const booked = new Set(bookedDates);
    const dates = [];
    const start = new Date(EARLIEST_EDITABLE);
    for (let i = 0; i < 60; i++) {
      const dt = new Date(start);
      dt.setDate(start.getDate() + i);
      const k = isoDate(dt);
      if (k !== dateKey && !booked.has(k)) dates.push(dt);
    }
    return dates;
  }, [dateKey, bookedDates]);

  const activeLines = ds.lines.filter((l) => l.product_id);
  const total       = activeLines.reduce((s, l) => s + (l.unit_price || 0) * (l.qty || 0), 0);

  return (
    <div className="pw-modal-overlay" onClick={onClose}>
      <div className="pw-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pw-modal-header">
          <h3 className="pw-modal-title">Re-schedule — {fmtDate(headerDate)}</h3>
          <button className="pw-modal-close" onClick={onClose} disabled={ds.moving}>&#215;</button>
        </div>

        <div className="pw-modal-body">
          {/* Read-only order summary */}
          <ul className="pw-summary-lines">
            {activeLines.map((l, i) => (
              <li key={i} className="pw-summary-line">
                <span className="pw-summary-line-name">{l.product_name}</span>
                <span className="pw-summary-line-right">
                  <span className="pw-summary-line-total">
                    {l.qty > 1 ? `×${l.qty} ` : ''}{fmtPrice(l.unit_price * l.qty)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          {activeLines.length > 0 && (
            <div className="pw-order-total">Total: {fmtPrice(total)}</div>
          )}

          {/* Date selector — occupied dates are excluded from the list */}
          <div className="pw-move-section">
            <select
              className="pw-move-select"
              value={moveTo}
              onChange={(e) => setMoveTo(e.target.value)}
              disabled={availableDates === null || ds.moving}
            >
              {availableDates === null ? (
                <option value="">Loading dates…</option>
              ) : (
                <>
                  <option value="">Select new date…</option>
                  {availableDates.map((dt) => {
                    const k = isoDate(dt);
                    return <option key={k} value={k}>{fmtDate(dt)}</option>;
                  })}
                </>
              )}
            </select>
          </div>

          {ds.saveError && <p className="pw-error">{ds.saveError}</p>}

          {confirming && (
            <div className="pw-cancel-confirm-box">
              <p className="pw-cancel-confirm-msg">
                Cancelling this order will credit{' '}
                <strong>{fmtPrice(total)}</strong> to your wallet. This cannot be undone.
              </p>
            </div>
          )}
        </div>

        <div className="pw-modal-footer">
          {confirming ? (
            <>
              <button
                className="pw-modal-cancel"
                onClick={() => setConfirming(false)}
                disabled={ds.cancelling}
              >
                Go Back
              </button>
              <button
                className="pw-cancel-order-btn pw-cancel-order-btn--confirm"
                onClick={() => onCancelOrder(dateKey)}
                disabled={ds.cancelling}
              >
                {ds.cancelling ? 'Cancelling…' : 'Confirm Cancellation'}
              </button>
            </>
          ) : (
            <>
              <button
                className="pw-cancel-order-btn"
                onClick={() => setConfirming(true)}
                disabled={ds.moving}
              >
                Cancel Order
              </button>
              <button className="pw-modal-cancel" onClick={onClose} disabled={ds.moving}>
                Close
              </button>
              <button
                className="pw-save-btn pw-save-btn--reschedule"
                onClick={() => onMove(dateKey, moveTo)}
                disabled={!moveTo || availableDates === null || ds.moving}
              >
                {ds.moving ? '…' : 'Re-schedule'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Copy-from-week modal
// ─────────────────────────────────────────────────────────
const CopyWeekModal = ({ days, onCopyWeek, onClose }) => {
  const [selectedMonday, setSelectedMonday] = useState('');
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState(null);

  const sourceOptions = useMemo(() => {
    const thisMonday = days[0];
    return Array.from({ length: 10 }, (_, i) => {
      const mon = new Date(thisMonday);
      mon.setDate(thisMonday.getDate() - (i + 1) * 7);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      const label =
        mon.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) +
        ' – ' +
        sun.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
      return { value: isoDate(mon), label };
    });
  }, [days]);

  const handleSubmit = async () => {
    if (!selectedMonday) return;
    setCopying(true);
    setError(null);
    try {
      await onCopyWeek(selectedMonday);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="pw-modal-overlay" onClick={onClose}>
      <div className="pw-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pw-modal-header">
          <h3 className="pw-modal-title">Copy from a previous week</h3>
          <button className="pw-modal-close" onClick={onClose} disabled={copying}>&#215;</button>
        </div>
        <div className="pw-modal-body">
          <p className="pw-copy-week-desc">
            Copies all orders from the selected week into{' '}
            <strong>{fmtWeekLabel(days)}</strong> as new Draft orders.
          </p>
          <div className="pw-move-section">
            <select
              className="pw-move-select"
              value={selectedMonday}
              onChange={(e) => { setSelectedMonday(e.target.value); setError(null); }}
              disabled={copying}
            >
              <option value="">Select week…</option>
              {sourceOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          {error && <p className="pw-error">{error}</p>}
        </div>
        <div className="pw-modal-footer">
          <button className="pw-modal-cancel" onClick={onClose} disabled={copying}>
            Cancel
          </button>
          <button
            className="pw-save-btn"
            onClick={handleSubmit}
            disabled={!selectedMonday || copying}
          >
            {copying ? 'Copying…' : 'Copy orders'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────
const PlanWeek = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const authHeader = { Authorization: `Bearer ${user?.token}` };

  const [weekOffset, setWeekOffset] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const days = getWeekDays(weekOffset);
  const loadedRef   = useRef(new Set());
  const todayCardRef = useRef(null);
  const todayIso     = isoDate(new Date());
  const [weekLoading, setWeekLoading] = useState(false);
  const [pageScrolled, setPageScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setPageScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const [replicating, setReplicating] = useState(false);
  const [bookedDates, setBookedDates] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [copyWeekOpen, setCopyWeekOpen] = useState(false);

  const [menus, setMenus] = useState([]);
  const [windows, setWindows] = useState([]);
  const [products, setProducts] = useState({});
  const [submenus, setSubmenus] = useState({});
  const [deliveryMinimum, setDeliveryMinimum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, dateKey: null });

  const [dayStates, setDayStates] = useState(() => {
    const init = {};
    getWeekDays(0).forEach((d) => { init[isoDate(d)] = defaultDayState(); });
    return init;
  });

  // Load menus and windows once
  useEffect(() => {
    const loadStatic = async () => {
      try {
        const [menusRes, windowsRes, deliveryRes] = await Promise.all([
          axios.get(`${API_URL}/plan-week/menus`,    { headers: authHeader }),
          axios.get(`${API_URL}/plan-week/windows`,  { headers: authHeader }),
          axios.get(`${API_URL}/menuRoutes/delivery-config`, { headers: authHeader }),
        ]);
        setMenus(menusRes.data);
        setWindows(windowsRes.data);
        const minVal = parseFloat(deliveryRes.data?.min_order_value);
        if (!isNaN(minVal)) setDeliveryMinimum(minVal);
      } catch {}
    };
    loadStatic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Disable browser scroll restoration so our scrollIntoView wins on initial navigation.
  // Without this, the browser asynchronously resets scroll to 0 after we call scrollIntoView.
  useEffect(() => {
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = prev; };
  }, []);

  // Scroll today's card to the top once data is ready (mobile UX).
  useEffect(() => {
    if (loading) return;
    if (weekOffset !== 0) return;

    const scroll = () =>
      todayCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Small delay lets React finish painting the grid before we scroll.
    const t = setTimeout(scroll, 100);
    return () => clearTimeout(t);
  }, [loading, weekOffset]);

  useEffect(() => {
    const from     = isoDate(days[0]);
    const to       = isoDate(days[days.length - 1]);
    const rangeKey = `${from}:${to}`;

    if (loadedRef.current.has(rangeKey)) {
      setLoading(false);
      setWeekLoading(false);
      return;
    }

    // Ensure every day in this week has a default state slot
    setDayStates((prev) => {
      const next = { ...prev };
      days.forEach((d) => {
        const k = isoDate(d);
        if (!next[k]) next[k] = defaultDayState();
      });
      return next;
    });

    if (weekOffset !== 0) setWeekLoading(true);

    const loadOrders = async () => {
      try {
        const ordersRes = await axios.get(`${API_URL}/plan-week/orders`, {
          params: { from, to },
          headers: authHeader,
        });

        loadedRef.current.add(rangeKey);

        if (ordersRes.data.length > 0) {
          setDayStates((prev) => {
            const next = { ...prev };
            ordersRes.data.forEach((order) => {
              const dateKey = order.delivery_date.slice(0, 10);
              if (!next[dateKey]) return;
              const orderLines = (order.lines || []).map((l) => ({
                category:     l.category   || null,
                product_id:   l.product_id,
                qty:          parseInt(l.qty, 10) || 1,
                unit_price:   parseFloat(l.unit_price) || 0,
                product_name: l.product_name,
              }));
              while (orderLines.length < 5) orderLines.push(emptyLine());
              next[dateKey] = {
                ...next[dateKey],
                window:         order.delivery_window || null,
                lines:          orderLines,
                savedOrderId:   order.id,
                saved:          true,
                order_lc_state: order.order_lc_state || null,
              };
            });
            return next;
          });

          const categoryIds = [
            ...new Set(
              ordersRes.data
                .flatMap((o) => (o.lines || []).map((l) => l.category))
                .filter(Boolean)
            ),
          ];
          if (categoryIds.length > 0) {
            const results = await Promise.all(
              categoryIds.map((id) =>
                axios
                  .get(`${API_URL}/plan-week/products`, {
                    params: { menu_id: id },
                    headers: authHeader,
                  })
                  .then((r) => [id, r.data])
                  .catch(() => null)
              )
            );
            const fetched = {};
            results.filter(Boolean).forEach(([id, data]) => { fetched[id] = data; });
            if (Object.keys(fetched).length > 0) {
              setProducts((prev) => ({ ...prev, ...fetched }));
            }
          }
        }
      } catch {}
      finally {
        setLoading(false);
        setWeekLoading(false);
      }
    };
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset, refreshKey]);

  const fetchProducts = useCallback(
    async (menuId, deliveryDate) => {
      const cacheKey = deliveryDate ? `${menuId}:${deliveryDate}` : menuId;
      if (products[cacheKey]) return;
      try {
        const params = { menu_id: menuId };
        if (deliveryDate) params.delivery_date = deliveryDate;
        const res = await axios.get(`${API_URL}/plan-week/products`, {
          params,
          headers: authHeader,
        });
        setProducts((prev) => ({ ...prev, [cacheKey]: res.data }));
      } catch {}
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products]
  );

  const fetchSubmenus = useCallback(
    async (menuId) => {
      if (submenus[menuId] !== undefined) return;
      try {
        const res = await axios.get(`${API_URL}/plan-week/submenus`, {
          params: { parent_id: menuId },
          headers: authHeader,
        });
        setSubmenus((prev) => ({ ...prev, [menuId]: res.data }));
      } catch {
        setSubmenus((prev) => ({ ...prev, [menuId]: [] }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [submenus]
  );

  const updateDay = (dateKey, patch) =>
    setDayStates((prev) => ({
      ...prev,
      [dateKey]: { ...prev[dateKey], ...patch },
    }));

  const handleSave = async (dateKey, linesOverride) => {
    const ds = dayStates[dateKey];
    if (!ds.window) {
      updateDay(dateKey, { saveError: 'Please select a delivery window.' });
      return;
    }
    const sourceLines = linesOverride !== undefined ? linesOverride : ds.lines;
    const validLines = sourceLines.filter((l) => l.product_id && parseInt(l.qty, 10) > 0);
    // Allow empty lines only when editing an existing order (will delete it)
    if (validLines.length === 0 && !ds.savedOrderId) {
      updateDay(dateKey, { saveError: 'Add at least one product.' });
      return;
    }
    updateDay(dateKey, { saving: true, saveError: null });
    try {
      const res = await axios.post(
        `${API_URL}/plan-week/save`,
        { delivery_date: dateKey, delivery_window: ds.window || null, lines: validLines },
        { headers: authHeader }
      );

      if (res.data.deleted) {
        // All lines were removed — order deleted, reset card to empty state
        setDayStates((prev) => ({
          ...prev,
          [dateKey]: {
            ...prev[dateKey],
            saving: false,
            saved: false,
            savedOrderId: null,
            saveError: null,
            lines: Array.from({ length: 5 }, emptyLine),
            window: null,
            moveTo: '',
          },
        }));
      } else {
        setDayStates((prev) => {
          const kept = [...validLines];
          while (kept.length < 5) kept.push(emptyLine());
          return {
            ...prev,
            [dateKey]: {
              ...prev[dateKey],
              saving: false,
              saved: true,
              savedOrderId: res.data.id,
              saveError: null,
              lines: kept,
              order_lc_state: 'Draft',
            },
          };
        });
      }
      setModal({ open: false, dateKey: null });
    } catch (err) {
      updateDay(dateKey, {
        saving: false,
        saveError: err.response?.data?.error || 'Could not save. Please try again.',
      });
    }
  };

  const handleMove = async (dateKey, toDate) => {
    if (!toDate) return;
    const ds = dayStates[dateKey];
    if (!ds.savedOrderId) return;
    updateDay(dateKey, { moving: true, saveError: null });
    try {
      const res = await axios.post(
        `${API_URL}/plan-week/move`,
        { from_date: dateKey, to_date: toDate },
        { headers: authHeader }
      );
      setDayStates((prev) => {
        const fromState = prev[dateKey];
        return {
          ...prev,
          [dateKey]: {
            ...prev[dateKey],
            lines:          Array.from({ length: 5 }, emptyLine),
            savedOrderId:   null,
            saved:          false,
            moving:         false,
            moveTo:         '',
            saveError:      null,
            order_lc_state: null,
          },
          [toDate]: {
            ...(prev[toDate] || defaultDayState()),
            lines:          fromState.lines,
            savedOrderId:   res.data.order_id,
            saved:          true,
            window:         fromState.window,
            saveError:      null,
            order_lc_state: fromState.order_lc_state,
          },
        };
      });
      setModal({ open: false, dateKey: null });
    } catch (err) {
      updateDay(dateKey, {
        moving: false,
        saveError: err.response?.data?.error || 'Could not move order.',
      });
    }
  };

  const handleCancelOrder = async (dateKey) => {
    const ds = dayStates[dateKey];
    if (!ds?.savedOrderId) return;
    updateDay(dateKey, { cancelling: true, saveError: null });
    try {
      const res = await axios.post(
        `${API_URL}/plan-week/cancel`,
        { order_id: ds.savedOrderId },
        { headers: authHeader }
      );
      const refunded = res.data.refunded || 0;
      setDayStates((prev) => ({
        ...prev,
        [dateKey]: { ...defaultDayState(), cancelSuccess: true, cancelledAmount: refunded },
      }));
      setModal({ open: false, dateKey: null });
      setTimeout(() => {
        setDayStates((prev) => ({
          ...prev,
          [dateKey]: { ...prev[dateKey], cancelSuccess: false, cancelledAmount: 0 },
        }));
      }, 6000);
    } catch (err) {
      updateDay(dateKey, {
        cancelling: false,
        saveError: err.response?.data?.error || 'Could not cancel order.',
      });
    }
  };

  // Active (non-cancelled) saved orders in the current week view
  const totalOrdersInWeek = useMemo(
    () => days.filter((d) => {
      const ds = dayStates[isoDate(d)];
      return ds?.saved && ds?.order_lc_state !== 'Cancelled';
    }).length,
    [days, dayStates]
  );

  // Draft orders in the current week view that meet their minimum — candidates for checkout
  const qualifyingOrders = useMemo(() => {
    return days
      .map((d) => {
        const k  = isoDate(d);
        const ds = dayStates[k];
        if (!ds || !ds.saved || ds.order_lc_state !== 'Draft') return null;
        const total = ds.lines
          .filter((l) => l.product_id)
          .reduce((s, l) => s + (l.unit_price || 0) * (l.qty || 0), 0);
        const minVal = totalOrdersInWeek === 1 ? (deliveryMinimum || 0) : 499;
        return total >= minVal ? { dateKey: k, savedOrderId: ds.savedOrderId } : null;
      })
      .filter(Boolean);
  }, [days, dayStates, totalOrdersInWeek, deliveryMinimum]);

  const modalMinOrderValue = useMemo(() => {
    if (user?.user_type === 'S') return 1;
    if (!modal.dateKey) return 0;
    const mds = dayStates[modal.dateKey];
    const otherOrders = totalOrdersInWeek - (mds?.savedOrderId ? 1 : 0);
    return otherOrders === 0 ? (deliveryMinimum || 0) : 499;
  }, [modal.dateKey, dayStates, totalOrdersInWeek, deliveryMinimum, user?.user_type]);

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const s    = document.createElement('script');
      s.src      = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload   = () => resolve(true);
      s.onerror  = () => resolve(false);
      document.body.appendChild(s);
    });

  const handlePywCheckout = async () => {
    setCheckingOut(true);
    setCheckoutError(null);
    let razorpay_order_id = null;
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Could not load payment gateway');

      const orderIds = qualifyingOrders.map((o) => o.savedOrderId);
      const res = await axios.post(
        `${API_URL}/plan-week/checkout/create-order`,
        { order_ids: orderIds },
        { headers: authHeader }
      );

      razorpay_order_id = res.data.razorpay_order_id;

      const rzpOptions = {
        key:      res.data.key,
        amount:   res.data.amount,
        currency: res.data.currency,
        order_id: razorpay_order_id,
        name:     'Local Heros Foods Pvt Ltd',
        description: `Plan Your Week — ${res.data.order_count} orders`,
        theme:    { color: '#C9A227' },
        config: {
          display: {
            blocks: { upiQr: { name: 'Pay via UPI QR Code', instruments: [{ method: 'upi', flows: ['qr'] }] } },
            sequence: ['block.upiQr'],
            preferences: { show_default_blocks: true },
          },
        },
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(
              `${API_URL}/plan-week/checkout/verify`,
              {
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
              },
              { headers: authHeader }
            );
            if (verifyRes.data.success) {
              // Flip qualifying orders to Placed immediately — no page reload needed
              setDayStates((prev) => {
                const next = { ...prev };
                qualifyingOrders.forEach(({ dateKey }) => {
                  if (next[dateKey]) next[dateKey] = { ...next[dateKey], order_lc_state: 'Placed' };
                });
                return next;
              });
            }
          } catch {
            setCheckoutError('Payment received — please refresh to see updated order status.');
          }
          setCheckingOut(false);
        },
        modal: {
          ondismiss: async () => {
            try {
              await axios.post(
                `${API_URL}/plan-week/checkout/cancel`,
                { razorpay_order_id },
                { headers: authHeader }
              );
            } catch {}
            setCheckingOut(false);
          },
        },
      };

      const rzp = new window.Razorpay(rzpOptions);
      rzp.on('payment.failed', () => {
        setCheckoutError('Payment failed. Please try again.');
        setCheckingOut(false);
      });
      rzp.open();
    } catch (err) {
      setCheckoutError(err.response?.data?.error || 'Checkout failed. Please try again.');
      setCheckingOut(false);
    }
  };

  const handleReplicate = async (fromDateKey, line) => {
    const targetDates = days
      .filter((d) => isoDate(d) !== fromDateKey && d >= EARLIEST_EDITABLE)
      .map((d) => isoDate(d));
    if (targetDates.length === 0) return;

    setReplicating(true);
    try {
      const res = await axios.post(
        `${API_URL}/plan-week/replicate`,
        { product_id: line.product_id, qty: line.qty, unit_price: line.unit_price, target_dates: targetDates },
        { headers: authHeader }
      );
      const results = res.data.results || [];
      setDayStates((prev) => {
        const next = { ...prev };
        results.forEach(({ date, action, order_id }) => {
          if (action !== 'created' && action !== 'added') return;
          const existing = next[date] || defaultDayState();
          if (existing.lines.some((l) => l.product_id === line.product_id)) return;
          const realLines = existing.lines.filter((l) => l.product_id);
          realLines.push({
            category:     line.category,
            product_id:   line.product_id,
            qty:          line.qty,
            unit_price:   line.unit_price,
            product_name: line.product_name,
          });
          while (realLines.length < 5) realLines.push(emptyLine());
          next[date] = { ...existing, lines: realLines, savedOrderId: order_id, saved: true, order_lc_state: existing.order_lc_state || 'Draft' };
        });
        return next;
      });
    } catch (err) {
      console.error('Replicate failed:', err);
    } finally {
      setReplicating(false);
    }
  };

  const openModal = (dateKey) => setModal({ open: true, dateKey, mode: 'edit' });

  const openReschedule = (dateKey) => {
    setBookedDates(null); // show "Loading dates…" immediately
    setModal({ open: true, dateKey, mode: 'reschedule' });
    axios
      .get(`${API_URL}/plan-week/booked-dates`, { headers: authHeader })
      .then((r) => setBookedDates(r.data || []))
      .catch(() => setBookedDates([]));
  };

  const handleCopyWeek = async (fromMonday) => {
    const toMonday = isoDate(days[0]);
    try {
      await axios.post(
        `${API_URL}/plan-week/copy-week`,
        { from_monday: fromMonday, to_monday: toMonday },
        { headers: authHeader }
      );
      // Clear the cache for this week and reset day states so the effect re-fetches
      const from = isoDate(days[0]);
      const to   = isoDate(days[days.length - 1]);
      loadedRef.current.delete(`${from}:${to}`);
      setDayStates((prev) => {
        const next = { ...prev };
        days.forEach((d) => { next[isoDate(d)] = defaultDayState(); });
        return next;
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (err.response?.status === 409) {
        const conflicts = err.response.data.conflicts || [];
        const dayNames  = conflicts.map((iso) => {
          const [y, m, d] = iso.split('-').map(Number);
          return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
            weekday: 'short', day: 'numeric', month: 'short',
          });
        });
        throw new Error(`Orders already exist on: ${dayNames.join(', ')}`);
      }
      throw new Error(err.response?.data?.error || 'Could not copy. Please try again.');
    }
  };

  const closeModal = () => {
    if (modal.dateKey) updateDay(modal.dateKey, { saveError: null });
    setModal({ open: false, dateKey: null, mode: null });
    setBookedDates(null);
  };

  return (
    <div className="pw-page">
      {pageScrolled && (
        <>
          <button
            className="pw-float-nav pw-float-nav--prev"
            onClick={() => setWeekOffset((o) => { const n = o - 1; if (n !== 0) window.scrollTo({ top: 0, behavior: 'smooth' }); return n; })}
            aria-label="Previous week"
          >&#8249;</button>
          <button
            className="pw-float-nav pw-float-nav--next"
            onClick={() => setWeekOffset((o) => { const n = o + 1; if (n !== 0) window.scrollTo({ top: 0, behavior: 'smooth' }); return n; })}
            aria-label="Next week"
          >&#8250;</button>
        </>
      )}
      <div className="pw-header">
        <button className="pw-back-btn" onClick={() => navigate('/home')}>
          &#8592; Back
        </button>
        <h2 className="pw-title">Plan ur Box</h2>
        {qualifyingOrders.length >= 1 && (
          <div className="pw-header-right">
            {checkoutError && <span className="pw-checkout-error">{checkoutError}</span>}
            <button
              className="pw-checkout-btn"
              onClick={handlePywCheckout}
              disabled={checkingOut || loading}
            >
              {checkingOut
                ? 'Processing…'
                : `Checkout (${qualifyingOrders.length} orders)`}
            </button>
          </div>
        )}
      </div>

      <div className="pw-week-nav">
        <button className="pw-week-nav-btn" onClick={() => setWeekOffset((o) => o - 1)}>
          &#8249; Prev
        </button>
        <span className="pw-week-label">{fmtWeekLabel(days)}</span>
        <button className="pw-week-nav-btn" onClick={() => setWeekOffset((o) => o + 1)}>
          Next &#8250;
        </button>
      </div>
      <div className="pw-copy-week-row">
        <button className="pw-copy-week-btn" onClick={() => setCopyWeekOpen(true)}>
          Copy from week
        </button>
      </div>

      {loading ? (
        <p className="pw-loading">Loading…</p>
      ) : (
        <>
          {weekLoading && <p className="pw-loading">Loading week…</p>}
          <div className="pw-grid">
            {days.map((d) => {
              const k = isoDate(d);
              return (
                <div key={k} ref={k === todayIso ? todayCardRef : null}>
                  <DayCard
                    d={d}
                    ds={dayStates[k] || defaultDayState()}
                    onOpen={openModal}
                    editable={d >= EARLIEST_EDITABLE}
                    onReplicate={handleReplicate}
                    replicating={replicating}
                    onReschedule={openReschedule}
                    onCancelOrder={handleCancelOrder}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}

      {modal.open && modal.dateKey && modal.mode === 'edit' && (
        <PlanModal
          dateKey={modal.dateKey}
          ds={dayStates[modal.dateKey]}
          menus={menus}
          windows={windows}
          products={products}
          submenus={submenus}
          minOrderValue={modalMinOrderValue}
          onFetchProducts={fetchProducts}
          onFetchSubmenus={fetchSubmenus}
          onUpdateDay={updateDay}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
      {modal.open && modal.dateKey && modal.mode === 'reschedule' && (
        <RescheduleModal
          dateKey={modal.dateKey}
          ds={dayStates[modal.dateKey]}
          bookedDates={bookedDates}
          onMove={handleMove}
          onCancelOrder={handleCancelOrder}
          onClose={closeModal}
        />
      )}
      {copyWeekOpen && (
        <CopyWeekModal
          days={days}
          onCopyWeek={handleCopyWeek}
          onClose={() => setCopyWeekOpen(false)}
        />
      )}
    </div>
  );
};

export default PlanWeek;
